import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'

import { extractNumber } from '@cred/shared/utils'

import StudentLoanFilterForm from 'student-loan/shared/filter-form'
import { getCtaUrl } from 'student-loan/shared/utils'
import withRateScript from 'components/hocs/with-rate-script'
import Pagination from 'components/styled-pagination'
import LendersList from 'components/lenders-list'
import { loadLendersRates, filterLender } from 'common/utils/rates'
import useActiveLenders from 'components/hooks/useActiveLenders'
import { INITIAL_VALUES, VERTICAL } from 'student-loan/rate-table/const'

import FilterModal from '../components/filter-modal'
import {
  generateFilterText,
  getLoanAmount,
  getPageStats,
  getPartnerDefaultFilterValues,
} from '../utils'

const PAGINATION_NAME = 'student-loan-multi-rate'
const LENDERS_PER_PAGE = 3
const ROLE_FALLBACK = 'Student or Co-signer'

/* Get lenders rates from Rates library. */
const getLenders = (availableLenders, props, formFilterValues) => {
  const { isScriptLoaded } = props
  const creditScore = false
  const loanAmount = getLoanAmount(formFilterValues.loanAmount)

  /* Get the rates after loading the ratesLibrary JS and do it only once. */
  if (isScriptLoaded) {
    return loadLendersRates({
      availableLenders: filterLender(availableLenders, creditScore, loanAmount),
      filters: { vertical: VERTICAL },
    })
  }

  return []
}

const StudentLoanRateTable = ({
  initialize,
  variation,
  theme,
  utms,
  widgetContext,
  omitUtmParams = false,
  metaQuery,
  ...props
}) => {
  const defaultValues = {
    ...INITIAL_VALUES,
    ...getPartnerDefaultFilterValues(widgetContext, 'student-loan'),
  }

  const { control, reset, getValues, formState } = useForm({ defaultValues, mode: 'onChange' })

  const [page, setPage] = useState(0)
  const [filterValues, setFilterValues] = useState(defaultValues)
  const availableLenders = useActiveLenders('in_school')

  const lenders = getLenders(availableLenders, props, filterValues)
  const { pageCount, pageFrom, pageTo, showPagination } = getPageStats(
    variation,
    lenders,
    page,
    LENDERS_PER_PAGE,
  )

  const cancel = (closeModal) => {
    closeModal()
    // Setting timeout so the form doesn't reset as the modal is disappearing
    setTimeout(() => {
      reset(filterValues)
    }, 500)
  }

  const onUpdate = (closeModal) => {
    const { role, loanAmount } = getValues()
    setFilterValues({ role, loanAmount: extractNumber(loanAmount) })
    closeModal()
  }

  const onPageChange = (_, selectedPage) => {
    setPage(selectedPage)
  }

  const onReset = () => {
    reset(defaultValues)
    setFilterValues(defaultValues)
  }

  return (
    <div>
      <FilterModal
        formName="student-loan-filter-form"
        filterText={generateFilterText(
          { loanAmount: filterValues.loanAmount, role: filterValues.role || ROLE_FALLBACK },
          VERTICAL,
        )}
      >
        {({ closeModal }) => (
          <StudentLoanFilterForm
            update={() => onUpdate(closeModal)}
            cancel={() => cancel(closeModal)}
            multiVertical
            withFooter
            control={control}
            invalid={!formState.isValid}
          />
        )}
      </FilterModal>
      <LendersList
        name="multiVerticalStudentLoan"
        theme={theme}
        lenders={lenders.slice(pageFrom, pageTo)}
        ctaUrl={getCtaUrl({ utms, theme, role: filterValues.role, omitUtmParams, metaQuery })}
        resetFilters={onReset}
      />
      {showPagination && (
        <Pagination onChange={onPageChange} name={PAGINATION_NAME} pageCount={pageCount} />
      )}
    </div>
  )
}

StudentLoanRateTable.propTypes = {
  initialize: PropTypes.func,
  variation: PropTypes.string,
  theme: PropTypes.string,
  utms: PropTypes.shape({
    utm_source: PropTypes.string,
    utm_campaign: PropTypes.string,
    utm_medium: PropTypes.string,
    utm_content: PropTypes.string,
    credclid: PropTypes.string,
    renderId: PropTypes.string,
  }),
  omitUtmParams: PropTypes.bool,
  page: PropTypes.number,
  widgetContext: PropTypes.object,
  metaQuery: PropTypes.string,
}

export { StudentLoanRateTable as StudentLoanRateTableNoHoc, getLenders }

export default withRateScript(StudentLoanRateTable)
